import React, { useCallback, useContext, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import { connect } from 'react-redux';
import { compose } from 'redux';

// GLOBAL VARIABLES
import { FORM_TABLE } from 'defaults.js';

// GLOBAL FUNCTIONS
import { doCallback } from 'functions';

// HELPERS
import { lookupOptions } from 'core/form/control/helpers/options/lookupOptions';

// CONTEXT
import { DropdownContext } from 'core/tools/dropdown/helpers/dropdownContext';

// TOOLS
import Button from 'core/tools/Button';
import Dropdown from 'core/tools/dropdown/Dropdown';

//STYLES
import styles from './addButton.module.scss';

// CHILD COMPONENT
const OptionButton = ({
  onClick,
  ...props
}) => {

  // CONTEXT
  const { setShow } = useContext(DropdownContext) || {};

  // CALLBACKS
  const handleClick = useCallback(
    () => {
      doCallback(onClick);
      setShow(false);
    },
    [onClick, setShow]
  )

  // RETURN
  return (
    <Button
      className={styles.optionButton}
      variant="custom"
      justify="start"
      size="sm"
      onClick={handleClick}
      {...props}
    />
  )
}

// MAIN COMPONENT
const AddButton = compose(
  lookupOptions,
  connect(
    ({ lookups }) => ({ lookups })
  )
)(({
  name,
  itemLabel = 'Item',
  itemConstructor,
  lookup,
  options,
  optionKeys,
  useCustom,
  button = {},
  // REDUX STATE
  lookups
}) => {

  // FORMIK CONTEXT
  const { values, setFieldValue } = useFormikContext() || {};

  // MEMOS
  const value = useMemo(
    () => cloneDeep(get(values, name)) || [],
    [name, values]
  )

  // CALLBACKS
  const addItem = useCallback(
    option => {
      const newValue = cloneDeep(value);
      const obj = lookups[lookup]?.find(item => item[optionKeys?.value] === option?.value);
      newValue.push(isFunction(itemConstructor) ? itemConstructor(obj) : itemConstructor);
      setFieldValue(name, newValue);
    },
    [name, value, lookups, lookup, optionKeys, itemConstructor, setFieldValue]
  )

  // RENDER
  return options ? (
    <Dropdown
      toggle={{
        label: `Add ${itemLabel}`,
        fullWidth: false,
        size: 'sm',
        ...FORM_TABLE.addButton
      }}
      menu={{
        className: styles.menu
      }}
      align="start"
      useArrow={false}
      useScrollbox
    >
      {useCustom && <>
        <OptionButton
          label={isString(useCustom) ? useCustom : `Add Custom ${itemLabel}`}
          onClick={() => addItem()}
        />
        <hr />
      </>}
      {options.map((option, i) =>
        <OptionButton
          key={i}
          label={option.label}
          onClick={() => addItem(option)}
        />
      )}
    </Dropdown>
  ) : (
    <Button.Add
      variant="secondary"
      label={`Add ${itemLabel}`}
      onClick={() => addItem()}
      fullWidth={false}
      size="sm"
      outline
      {...button}
    />
  )
})

// EXPORT
export default AddButton
